<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import OwnerLayout from './layouts/OwnerLayout.vue'
import PublicLayout from './layouts/PublicLayout.vue'
import GlobalSnackbar from '@/components/GlobalSnackbar.vue'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const layout = computed(() => route.meta.layout ?? 'public')
/** Remounts owner pages per account, discarding any previous owner's loaded state. */
const ownerKey = computed(() => (route.meta.requiresAuth ? (auth.user?.id ?? 'none') : 'public'))
/** Owner pages render only with a session; nothing is requested while the guard redirects. */
const showPage = computed(() => !route.meta.requiresAuth || auth.isSignedIn)

// The account changed without this tab's own sign-out (expiry, another tab signing out or
// in as someone else). Leave owner pages so no previous-owner data stays on screen.
watch(
  () => auth.user?.id,
  (next, previous) => {
    if (!previous || next === previous || !route.meta.requiresAuth) return
    if (next) void router.replace({ name: 'surveys' })
    else if (auth.sessionEnd) {
      void router.replace({ name: 'login', query: { redirect: route.fullPath } })
    }
  },
)
</script>

<template>
  <v-app>
    <OwnerLayout v-if="layout === 'owner'" :key="ownerKey">
      <router-view v-if="showPage" />
    </OwnerLayout>
    <PublicLayout v-else-if="layout === 'public'">
      <router-view />
    </PublicLayout>
    <v-main v-else>
      <router-view v-if="showPage" :key="ownerKey" />
    </v-main>
    <GlobalSnackbar />
  </v-app>
</template>
